import styled, { css } from 'styled-components';

import { ACQ, COLORS } from '../../theme/tokens';
import { hoverWash } from '../../theme/mixins';
import { Button } from '../Button/Button';
import { Text } from '../Text/Text';

/* The card reflows on its OWN width, not the viewport. The studio chrome (side
   menu 84px + Lumio panel 380px + gaps/padding) shrinks the CONTENT area far below
   the viewport, so a viewport media query left the horizontal row cramped across
   several bands: mobile chrome (~821-899px) AND Lumio-open desktops (~1280-1380px),
   where content drops under the row's ~760px minimum while the viewport is still
   wide. An inline-size container query (see `Root` below) stacks exactly when the
   card's box is too narrow, in every band. 860px clears the row comfortably (the
   bar gets room for both markers, the three benefit columns clear the bar).
   Pattern mirrors pages/acquisition Performance.styles.ts. */
const STACK = '@container (max-width: 860px)';

/* Containment frame: establishes the inline-size container the card measures itself
   against. Carries no visual styling; the surface lives on `Card`. */
export const Root = styled.div`
  container-type: inline-size;
`;

export const Card = styled.div<{ $clickable: boolean }>`
  /* Figma lays the row out as three flush regions (no inter-region gap): the
     identity+bar group, the unlock-benefits block and the CTA, summing to the
     card width. The benefits block's own 12px padding supplies the breathing
     room between it and its neighbours, so the regions butt together. */
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px;
  border-radius: 16px;
  /* The standard inner-card surface (#18181b), the same one the player rows and
     campaign/promotion cards use, so the card sits a clear step lighter than the
     #101012 Container it now nests in (was a darker gradient for floating on the
     bare page). */
  background: ${COLORS.surfaceCard};
  overflow: hidden;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  /* Whole-card affordance: the shared cursor-following glow + proximity-lit border
     ring (see theme/mixins.hoverWash), lit only when the card opens the feature. */
  ${(p) =>
    p.$clickable &&
    css`
      ${hoverWash}
    `}

  ${STACK} {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }
`;

/* Region A: artwork + comparison bar as one unit, the same width as the benefits
   region (Figma splits the row evenly before the CTA). The bar grows to fill the
   space beside the identity so it stretches back toward the icon group rather than
   sitting pinned at the right edge. On mobile it is the full-width top row. */
export const TopRow = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;

  ${STACK} {
    flex: 0 0 auto;
    width: 100%;
    gap: 0;
  }
`;

export const Identity = styled.div`
  flex: 0 0 auto;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
`;

/** Positioning frame for the artwork so an alert dot can sit at its top-right corner. */
export const ArtWrap = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
`;

export const Art = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
`;

/** Attention dot on the artwork's top-right corner — mirrors the Product sidebar dot so the
 *  owner links the two, flagging a feature with a payment to finish. Ringed in the card
 *  surface so it reads as a badge over the artwork. */
export const AlertDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${COLORS.statusDanger};
  box-shadow: 0 0 0 2px ${COLORS.surfaceCard};
`;

export const Name = styled(Text)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.15;
  color: ${COLORS.featureName};
`;

export const Status = styled(Text)<{ $color: string }>`
  font-size: 10px;
  line-height: 1.2;
  color: ${(p) => p.$color};
`;

/* Grows to fill Region A beside the identity (the comparison bar stretches back
   toward the icon group). */
export const BarSlot = styled.div`
  flex: 1 1 auto;
  min-width: 180px;

  ${STACK} {
    min-width: 0;
  }
`;

/* Region B: equal width to Region A on desktop — Figma splits the row evenly
   between the two before the CTA. The 12px inset lives on the inner block, NOT
   here: padding on a flex-basis:0 item can't shrink below itself, which would
   make this region a constant 24px wider than the unpadded Region A. */
export const Benefits = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;

  ${STACK} {
    flex: 0 0 auto;
    width: 100%;
  }
`;

/* The unlock divider + benefit deltas, inset 12px inside Region B (Figma: a
   367px-wide benefit row within the 391px region). Styled to match the Operations
   "Activation Benefits" block verbatim — same header, fonts, colours and the
   solid (not dashed) hairlines. */
export const BenefitsInner = styled.div`
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
  border-radius: 16px;
`;

/* Unlock header: a centred 296px block — the uppercase label flanked by solid
   hairlines (mirrors Operations BenefitsHeader / HeaderLine / HeaderLabel). */
export const UnlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 296px;
  max-width: 100%;
`;

export const UnlockLine = styled.div`
  flex: 1 1 0;
  min-width: 1px;
  height: 1px;
  background: ${ACQ.divider};
`;

export const UnlockLabel = styled(Text)`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${COLORS.unlockLabel};
`;

export const BenefitRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;
  width: 100%;
`;

export const Benefit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

/* Delta figure, baseline-aligned (Operations BenefitValue: 24px / 400). */
export const BenefitDelta = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  line-height: 1.1;
  color: ${COLORS.benefitPositive};
`;

export const BenefitValue = styled(Text)`
  font-size: 24px;
  font-weight: 400;
`;

export const BenefitLabel = styled(Text)`
  width: 70px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.25;
  text-align: center;
  color: ${ACQ.t400};
`;

// Sizing only — the shared <Button variant="outline"> owns the surface, border,
// text colour and their light/dark + accent theming (no bespoke colour overrides,
// which previously forced white text on a white pill in light mode).
export const Cta = styled(Button)`
  flex: 0 0 auto;
  min-width: 150px;
  height: 48px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;

  ${STACK} {
    width: 100%;
  }
`;
